import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { brand } from "../../theme/theme";
import {
  Box,
  Typography,
  IconButton,
  Chip,
  Stack,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "../../components/DropdownMenu";
import {
  Heart,
  MessageCircle,
  CheckSquare,
  Archive,
  MoreVertical,
  ExternalLink,
  FileText,
  Video,
  Image as ImageIcon,
  Paperclip,
  AlertTriangle,
  MessageSquare,
  Link as LinkIcon,
  X,
  Download,
  Eye,
  Check,
  CheckCircle2,
  ThumbsUp,
  Edit,
  Trash2,
  Send,
} from "lucide-react";

import { Avatar, AvatarFallback } from "../../components/Avatar";
import { Button } from "../../components/Button";
import { CustomTextArea } from "../../components/CustomTextArea";
import { ThreadComment } from "./ThreadComment";
import { ConfirmModal } from "../../components/ConfirmModal";
import apiClient, {
  deleteAttachment,
  downloadAttachment,
  getAttachmentPreviewUrl,
} from "../../api-client";
import { AttachmentUploader } from "../../components/AttachmentUploader";
import {
  CustomMentionsInput,
  getRoleDisplayName,
  createMentionUsers,
  formatContentWithMentions,
  hasValidContent,
} from "../../components/CustomMentionsInput";
import { usePasteImage } from "../../components/hooks/usePasteImage";
import { LikersTooltip } from "../../components/LikersTooltip";
import {
  messageContentTypographySx,
  shortenLongUrlsInText,
  truncateMessageContent,
} from "./messageContentUtils";
import { formatFileSize } from "../../utils/fileSize";

const MAX_POST_PREVIEW_LINES = 6;
const APPROX_CHARS_PER_LINE = 70;
const MAX_POST_PREVIEW_CHARS = MAX_POST_PREVIEW_LINES * APPROX_CHARS_PER_LINE;

const getTagColor = (tag) => {
  switch (tag.toLowerCase()) {
    case "urgente":
      return { background: "#fecaca", text: "#dc2626", border: "#fca5a5" };
    case "formativo":
      return { background: "#E3F6EA", text: "#0F9D58", border: "#C4E8D2" };
    case "solucionado":
      return { background: "#dbeafe", text: "#1e40af", border: "#bfdbfe" };
    default:
      return { background: "#f3f4f6", text: "#1f2937", border: "#e5e7eb" };
  }
};

// Helper para determinar el tipo de archivo basado en la extensión o MIME type
const getFileType = (attachment) => {
  const fileName =
    attachment.original_filename ||
    attachment.file_name ||
    attachment.name ||
    "";
  const mimeType = attachment.file_type || attachment.type || "";

  // Verificar por MIME type
  if (mimeType.startsWith("image/")) return "image";
  if (mimeType.startsWith("video/")) return "video";
  if (
    mimeType.includes("pdf") ||
    mimeType.includes("document") ||
    mimeType.includes("text")
  )
    return "document";

  // Verificar por extensión
  const extension = fileName.toLowerCase().split(".").pop();
  const imageExts = ["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg"];
  const videoExts = ["mp4", "webm", "ogg", "avi", "mov", "mkv"];
  const docExts = ["pdf", "doc", "docx", "xls", "xlsx", "txt", "csv"];

  if (imageExts.includes(extension)) return "image";
  if (videoExts.includes(extension)) return "video";
  if (docExts.includes(extension)) return "document";

  return "other";
};

const getAttachmentIcon = (attachment) => {
  const type = getFileType(attachment);
  switch (type) {
    case "video":
      return <Video size={20} />;
    case "image":
      return <ImageIcon size={20} />;
    case "document":
      return <FileText size={20} />;
    case "link":
      return <ExternalLink size={20} />;
    default:
      return <Paperclip size={20} />;
  }
};

// Función helper para calcular tiempo relativo
const getRelativeTime = (timestamp) => {
  const now = new Date();
  const time = new Date(timestamp);
  const diffInSeconds = Math.floor((now - time) / 1000);
  const diffInDays = Math.floor(diffInSeconds / 86400);

  if (diffInSeconds < 60) return "hace un momento";
  if (diffInSeconds < 3600) return `hace ${Math.floor(diffInSeconds / 60)} min`;
  if (diffInSeconds < 86400) return `hace ${Math.floor(diffInSeconds / 3600)} h`;

  // Menos de 7 días: mostrar en días
  if (diffInDays < 7) {
    return diffInDays === 1 ? "hace 1 día" : `hace ${diffInDays} días`;
  }

  // De 7 a 29 días: mostrar en semanas
  if (diffInDays < 30) {
    const weeks = Math.floor(diffInDays / 7);
    return weeks === 1 ? "hace 1 semana" : `hace ${weeks} semanas`;
  }

  // 30 días o más: mostrar en meses
  const months = Math.floor(diffInDays / 30);
  return months === 1 ? "hace 1 mes" : `hace ${months} meses`;
};

// Helper para formatear el tamaño de archivo
// Componente ReadIndicator eliminado - el sistema de lectura sigue funcionando en el backend

// Componente para mostrar un adjunto individual
export const AttachmentDisplay = ({
  attachment,
  canDelete = false,
  onDelete,
}) => {
  const theme = useTheme();
  const [showPreview, setShowPreview] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileType = getFileType(attachment);
  const messageId = attachment.message_id || attachment.messageId;
  const fileName =
    attachment.original_filename ||
    attachment.file_name ||
    attachment.name ||
    "Archivo sin nombre";
  const fileSize = attachment.file_size || attachment.size;
  const fileUrl = previewUrl;

  useEffect(() => {
    let isActive = true;
    let objectUrl = null;

    if (fileType !== "image" || !messageId || !attachment.id) {
      setPreviewUrl(null);
      return undefined;
    }

    const loadPreview = async () => {
      try {
        objectUrl = await getAttachmentPreviewUrl(messageId, attachment.id);
        if (isActive) {
          setPreviewUrl(objectUrl);
        } else if (objectUrl) {
          window.URL.revokeObjectURL(objectUrl);
        }
      } catch (error) {
        if (isActive) {
          setPreviewUrl(null);
        }
      }
    };

    loadPreview();

    return () => {
      isActive = false;
      if (objectUrl) {
        window.URL.revokeObjectURL(objectUrl);
      }
    };
  }, [attachment.id, fileType, messageId]);

  const handleDelete = async () => {
    try {
      if (!messageId) {
        throw new Error("No se pudo identificar el mensaje del adjunto");
      }
      await deleteAttachment(messageId, attachment.id);
      if (onDelete) onDelete(attachment.id);
    } catch (error) {
      console.error("Error al eliminar adjunto:", error);
      alert(error.message || "Error al eliminar el adjunto");
    }
  };

  const handleDownload = async () => {
    try {
      if (!messageId) {
        throw new Error("No se pudo descargar el archivo");
      }
      await downloadAttachment(messageId, attachment.id);
    } catch (error) {
      console.error("Error al descargar adjunto:", error);
      alert(error.message || "Error al descargar el archivo");
    }
  };

  const handleImageClick = () => {
    if (fileType === "image" && fileUrl) {
      setShowPreview(true);
    }
  };

  return (
    <>
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: "8px",
          padding: "8px",
          backgroundColor: theme.palette.action.hover,
          borderRadius: "6px",
          border: `1px solid ${theme.palette.divider}`,
          maxWidth: "100%",
          width: "fit-content",
          cursor: fileType === "image" && fileUrl ? "pointer" : "default",
          "&:hover": {
            backgroundColor:
              fileType === "image" && fileUrl
                ? theme.palette.action.selected
                : theme.palette.action.hover,
          },
        }}
        onClick={(event) => {
          event.stopPropagation();
          handleImageClick();
        }}
      >
        {/* Vista previa o icono */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: fileType === "image" && fileUrl ? "36px" : "32px",
            height: fileType === "image" && fileUrl ? "36px" : "32px",
            borderRadius: "4px",
            backgroundColor: theme.palette.background.paper,
            overflow: "hidden",
            flexShrink: 0,
          }}
        >
          {fileType === "image" && fileUrl ? (
            <img
              src={fileUrl}
              alt={fileName}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
              onError={(e) => {
                // Si la imagen falla al cargar, mostrar el icono
                e.target.style.display = "none";
                e.target.nextSibling.style.display = "flex";
              }}
            />
          ) : null}
          <Box
            sx={{
              color: theme.palette.primary.main,
              display: fileType === "image" && fileUrl ? "none" : "flex",
            }}
          >
            {getAttachmentIcon(attachment)}
          </Box>
        </Box>

        {/* Información del archivo */}
        <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
          <Typography
            variant="body2"
            title={fileName} // Mostrar nombre completo en tooltip
            sx={{
              fontWeight: 500,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              fontSize: "0.8rem",
            }}
          >
            {fileName}
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: theme.palette.text.secondary, fontSize: "0.7rem" }}
          >
            {formatFileSize(fileSize)}
          </Typography>
        </Box>

        {/* Acciones */}
        <Box sx={{ display: "flex", alignItems: "center", gap: "2px" }}>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleDownload();
            }}
            sx={{
              padding: "4px",
              color: theme.palette.text.secondary,
              "&:hover": {
                color: theme.palette.primary.main,
                backgroundColor: theme.palette.primary.main + "0a",
              },
            }}
            title="Descargar"
          >
            <Download size={14} />
          </IconButton>

          {canDelete && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleDelete();
              }}
              sx={{
                padding: "4px",
                color: theme.palette.text.secondary,
                "&:hover": {
                  color: theme.palette.error.main,
                  backgroundColor: theme.palette.error.main + "0a",
                },
              }}
              title="Eliminar adjunto"
            >
              <X size={14} />
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Modal de vista previa de imagen */}
      {showPreview && fileType === "image" && fileUrl && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            cursor: "pointer",
          }}
          onClick={() => setShowPreview(false)}
        >
          <Box
            sx={{
              position: "relative",
              maxWidth: "90vw",
              maxHeight: "90vh",
            }}
          >
            <img
              src={fileUrl}
              alt={fileName}
              style={{
                maxWidth: "90vw",
                maxHeight: "85vh",
                width: "auto",
                height: "auto",
                objectFit: "contain",
                borderRadius: "8px",
              }}
            />
            <IconButton
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                color: "white",
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.7)",
                },
              }}
              onClick={(e) => {
                e.stopPropagation();
                setShowPreview(false);
              }}
            >
              <X size={24} />
            </IconButton>
          </Box>
        </Box>
      )}
    </>
  );
};

export function ThreadPost({
  post,
  onComment,
  onUpdateMessageType,
  onDeleteAttachment,
  onLike,
  onEditMessage,
  onDeleteMessage,
  onEditComment,
  onDeleteComment,
  onShowNotification = null,
  onAssignTask,
  onMoveToNewThread = null,
  onArchiveEvent = null,
  consultants = [],
  currentUser = null,
  allUsers = [],
  participants = [],
  setMessageRef,
  isHighlighted = false,
  readOnly = false,
  allowAssignTask = true,
  conversationFontSize = null, // Tamaño de letra personalizado en píxeles
}) {
  const theme = useTheme();
  const [showComments, setShowComments] = useState(post.comments.length > 0);
  const [newComment, setNewComment] = useState("");
  const [commentAttachments, setCommentAttachments] = useState([]);
  const [isCommenting, setIsCommenting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState(post.content);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showFullPost, setShowFullPost] = useState(false);
  const [threadParticipants, setThreadParticipants] = useState(participants);
  const [loadingParticipants, setLoadingParticipants] = useState(false);
  const [composerFocused, setComposerFocused] = useState(false);
  const hasFetchedParticipantsRef = useRef(false);

  // Helper para obtener el tamaño de letra a usar
  const getMessageFontSize = () => {
    if (conversationFontSize && typeof conversationFontSize === 'number') {
      const safeSize = Math.min(conversationFontSize, 16);
      return `${safeSize}px`;
    }
    return "0.85rem"; // Fallback al tamaño por defecto
  };

  // Helper para calcular tamaño del nombre del autor (proporcional al tamaño de mensaje)
  const getAuthorFontSize = () => {
    if (conversationFontSize && typeof conversationFontSize === 'number') {
      // El nombre del autor es ligeramente más grande que el mensaje (ratio ~1.0)
      const safeSize = Math.min(conversationFontSize, 16);
      return `${safeSize}px`;
    }
    return "0.85rem"; // Fallback
  };

  // Helper para calcular tamaño del timestamp (proporcional pero más pequeño)
  const getTimestampFontSize = () => {
    if (conversationFontSize && typeof conversationFontSize === 'number') {
      // El timestamp es ~88% del tamaño del mensaje
      const safeSize = Math.min(conversationFontSize, 16);
      return `${Math.round(safeSize * 0.88)}px`;
    }
    return "0.75rem"; // Fallback
  };

  const messageFontSize = getMessageFontSize();
  const authorFontSize = getAuthorFontSize();
  const timestampFontSize = getTimestampFontSize();

  // Lazy load thread participants solo cuando el usuario hace focus en el composer
  useEffect(() => {
    const fetchThreadParticipants = async () => {
      // Solo fetch si:
      // 1. Hay threadId (global wall messages)
      // 2. El usuario ha hecho focus en el composer
      // 3. No tenemos participants ya cargados (ni de props ni lazy)
      // 4. No estamos ya cargando
      if (!post.threadId || !composerFocused || loadingParticipants) {
        return;
      }

      // Solo fetch si no tenemos participants de ninguna fuente
      if (
        participants.length > 0 ||
        threadParticipants.length > 0 ||
        hasFetchedParticipantsRef.current
      ) {
        return;
      }

      setLoadingParticipants(true);
      try {
        const response = await apiClient.get(`communication/threads/${post.threadId}/`);
        const thread = response.data;
        if (thread.participants && Array.isArray(thread.participants)) {
          setThreadParticipants(thread.participants);
        }
      } catch (error) {
        console.error("[ThreadPost] Error fetching thread participants:", error);
        // No mostramos error al usuario, simplemente no tendrá autocomplete de menciones
      } finally {
        hasFetchedParticipantsRef.current = true;
        setLoadingParticipants(false);
      }
    };

    fetchThreadParticipants();
  }, [post.threadId, composerFocused, threadParticipants.length, participants.length, loadingParticipants]);

  const showNotification = useCallback(
    ({ message = "", isError = false } = {}) => {
      if (!message) return;
      if (onShowNotification) {
        onShowNotification({ message, isError });
      } else if (isError) {
        alert(message);
      } else {
        console.info(message);
      }
    },
    [onShowNotification]
  );

  // Hook para detectar imágenes pegadas desde el portapapeles
  const handlePasteImage = usePasteImage(setCommentAttachments, {
    onError: (msg) => showNotification({ message: msg, isError: true }),
    disabled: readOnly,
  });

  const isClientUser = currentUser?.role === "client";
  const allowInteractions = !readOnly;
  const canArchiveEvent =
    allowInteractions &&
    typeof onArchiveEvent === "function" &&
    post.messageType === "event" &&
    ["superadmin", "agency_admin", "consultant"].includes(currentUser?.role);
  const commentCount = Array.isArray(post.comments) ? post.comments.length : 0;
  const likeCount = Number(post.likeCount) || 0;

  // Crear lista de usuarios para menciones usando el helper reutilizable
  const mentionUsers = useMemo(() => {
    // En Conversations, el listado completo suele venir por `participants` (ThreadInterface).
    // En GlobalWall, se cargan lazy via threadParticipants.
    // Lo inyectamos como "clientData.participants" para reutilizar el helper.
    return createMentionUsers(consultants, allUsers, { participants: threadParticipants }, currentUser);
  }, [consultants, allUsers, threadParticipants, currentUser]);

  // Ahora usamos la información del autor que viene en el post mapeado
  const author = {
    name: post.author || "Usuario",
    role: post.authorRole || "user",
    profileImage: post.authorProfileImage || null,
  };

  // Verificar si es un mensaje de evento del sistema
  const isSystemEvent = post.messageType === "event" && !post.sender;

  // Obtener información del tipo de mensaje
  const getMessageTypeInfo = (messageType) => {
    switch (messageType) {
      case "important":
        return {
          label: "Importante",
          icon: <AlertTriangle size={14} />,
          color: { background: "#fef3c7", text: "#d97706", border: "#fde68a" },
        };
      case "action":
        return {
          label: "Asignado a:",
          icon: <CheckSquare size={14} />,
          color: { background: "#dbeafe", text: "#1e40af", border: "#bfdbfe" },
        };
      default:
        return null;
    }
  };

  const messageTypeInfo = getMessageTypeInfo(post.messageType);

  const postContent = post.content || "";
  const truncatedPostContent = useMemo(() => {
    return truncateMessageContent(postContent, MAX_POST_PREVIEW_CHARS);
  }, [postContent]);
  const shouldTruncatePost = truncatedPostContent.wasTruncated;
  const displayPostContent =
    shouldTruncatePost && !showFullPost
      ? shortenLongUrlsInText(truncatedPostContent.text)
      : postContent;

  useEffect(() => {
    setShowFullPost(false);
  }, [post.id, post.content]);

  const handleComment = () => {
    // Usar helper reutilizable para validar contenido
    const hasText = hasValidContent(newComment);
    const hasAttachments = commentAttachments.length > 0;

    if (hasText || hasAttachments) {
      onComment(post.id, newComment, commentAttachments);
      setNewComment("");
      setCommentAttachments([]);
      setIsCommenting(false);
      setShowComments(true); // Asegurar que se muestran los comentarios después de enviar
    }
  };


  const handleToggleImportant = () => {
    if (onUpdateMessageType) {
      const nextType =
        post.messageType === "important" ? "normal" : "important";
      onUpdateMessageType(post.id, nextType, null);
    }
  };

  const handleAssignTask = () => {
    if (onAssignTask) {
      onAssignTask(post, consultants);
      return;
    }

    if (onShowNotification) {
      onShowNotification({
        message: "La asignación de tareas no está disponible en este contexto.",
        isError: true,
      });
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
    setEditedContent(post.content);
  };

  const handleSaveEdit = async () => {
    // Validar que el contenido no esté vacío
    const trimmedContent = editedContent.trim();
    if (!trimmedContent) {
      alert("El contenido del mensaje no puede estar vacío");
      return;
    }

    try {
      if (!onEditMessage) {
        throw new Error(
          "No se puede editar el mensaje en este contexto."
        );
      }

      await onEditMessage(post.id, trimmedContent);

      setEditedContent(trimmedContent);
      setIsEditing(false);
      showNotification({
        message: "Mensaje actualizado correctamente",
        isError: false,
      });
    } catch (error) {
      console.error("Error al actualizar mensaje:", error);
      const errorMessage =
        error?.message ||
        error.response?.data?.detail ||
        "Error al actualizar el mensaje. Intenta nuevamente.";
      showNotification({ message: errorMessage, isError: true });
    }
  };

  const handleCancelEdit = () => {
    setEditedContent(post.content);
    setIsEditing(false);
  };

  const handleDelete = async () => {
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    setIsDeleting(true);
    try {
      if (!onDeleteMessage) {
        throw new Error(
          "No se puede eliminar el mensaje en este contexto."
        );
      }

      await onDeleteMessage(post.id);
      setShowDeleteModal(false);
      showNotification({
        message: "Mensaje eliminado correctamente",
        isError: false,
      });
    } catch (error) {
      console.error('Error al eliminar mensaje:', error);
      const errorMessage = error?.message || error.response?.data?.detail || 'Error al eliminar el mensaje. Intenta nuevamente.';
      setShowDeleteModal(false);
      showNotification({ message: errorMessage, isError: true });
    } finally {
      setIsDeleting(false);
    }
  };

  const isOwnMessage = useMemo(() => {
    if (!currentUser) return false;
    return (
      String(post.authorId) === String(currentUser.id) ||
      post.author === currentUser.name
    );
  }, [currentUser, post.author, post.authorId]);

  // Helper para obtener el nombre del consultor asignado
  const getAssignedConsultantName = (actionAssignedTo) => {
    // Si actionAssignedTo es un objeto con full_name, usarlo directamente
    if (
      actionAssignedTo &&
      typeof actionAssignedTo === "object" &&
      actionAssignedTo.full_name
    ) {
      return actionAssignedTo.full_name;
    }

    // Si actionAssignedTo es un ID, buscar el consultor en la lista
    if (
      actionAssignedTo &&
      (typeof actionAssignedTo === "number" ||
        typeof actionAssignedTo === "string")
    ) {
      const consultant = consultants.find((c) => c.id == actionAssignedTo);
      if (consultant && consultant.full_name) {
        return consultant.full_name;
      }
    }

    return "Nadie";
  };

  // Helper para verificar si el usuario actual puede asignar tareas
  const canAssignTask = () => {
    if (!currentUser) return false;

    // Superadmin y agency_admin siempre pueden editar
    if (["superadmin", "agency_admin"].includes(currentUser.role)) {
      return true;
    }

    // Para consultores: solo pueden editar SUS PROPIOS mensajes
    if (currentUser.role === "consultant") {
      if (!isOwnMessage) {
        return false; // No puede editar mensajes de otros
      }

      // Si es mensaje action y está asignado a OTRO consultor, NO puede editarlo
      if (post.messageType === "action" && post.actionAssignedTo) {
        const assignedToId = post.actionAssignedTo?.id || post.actionAssignedTo;
        return assignedToId == currentUser.id; // Solo si se lo asignó a sí mismo
      }

      // Puede editar sus propios mensajes normal e important
      return true;
    }

    return false;
  };

  const canToggleImportant = () => {
    if (!currentUser) return false;

    if (["superadmin", "agency_admin"].includes(currentUser.role)) {
      return true;
    }

    if (currentUser.role === "consultant") {
      if (!isOwnMessage) return false;
      if (post.messageType === "action" && post.actionAssignedTo) {
        const assignedToId = post.actionAssignedTo?.id || post.actionAssignedTo;
        return assignedToId == currentUser.id;
      }
      return true;
    }

    if (currentUser.role === "client") {
      return isOwnMessage;
    }

    return false;
  };

  const allowMoveToNewThread =
    !readOnly &&
    typeof onMoveToNewThread === "function" &&
    !!currentUser &&
    ["superadmin", "agency_admin", "consultant"].includes(currentUser.role);
  const isAlreadySynchronizedThread = !!post?.isThread;

  // Helper para verificar si se debe mostrar el menú (si tiene algún permiso)
  const shouldShowMenu = () => {
    if (!allowInteractions) return false;

    // Show menu if user can toggle important, assign task, or edit their own message
    return (
      allowMoveToNewThread ||
      canToggleImportant() ||
      canAssignTask() ||
      isOwnMessage
    );
  };

  // Usar helper reutilizable para formatear contenido con menciones
  const formatContent = (content) => {
    return formatContentWithMentions(content, theme);
  };

  const highlightedStyles = isHighlighted
    ? {
      backgroundColor: `${theme.palette.primary.light}22`,
      boxShadow: `0 0 0 2px ${theme.palette.primary.light}55`,
      transition: "background-color 0.6s ease, box-shadow 0.6s ease",
    }
    : {};

  // Renderizado especial para eventos del sistema
  if (isSystemEvent) {
    // Verificar si el mensaje contiene "Completed" o "completada"
    const isCompletedEvent = post.content &&
      (post.content.toLowerCase().includes('completed') ||
        post.content.toLowerCase().includes('completada'));

    return (
      <Box
        ref={(el) => setMessageRef && setMessageRef(post.id, el)}
        data-message-id={post.id}
        sx={{
          paddingY: "4px",
          paddingX: "20px",
          marginX: "20px",
          marginY: "6px",
          backgroundColor:
            theme.palette.mode === "dark"
              ? "rgba(255, 255, 255, 0.02)"
              : "rgba(0, 0, 0, 0.02)",
          borderRadius: "6px",
          "&:hover .event-message-hover-action": {
            opacity: 1,
          },
          ...highlightedStyles,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "12px",
          }}
        >
          {/* Contenido del evento */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              flex: 1,
              minWidth: 0,
            }}
          >
            {isCompletedEvent ? (
              <Check
                size={12}
                style={{
                  color: "#0F9D58",
                  flexShrink: 0,
                }}
              />
            ) : (
              <Box
                sx={{
                  width: 4,
                  height: 4,
                  borderRadius: "50%",
                  backgroundColor: theme.palette.text.secondary,
                  opacity: 0.5,
                  flexShrink: 0,
                }}
              />
            )}
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
                fontStyle: "italic",
                fontSize: messageFontSize,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {post.content}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                opacity: 0.6,
                fontSize: timestampFontSize,
                flexShrink: 0,
              }}
            >
              {getRelativeTime(post.timestamp)}
            </Typography>
          </Box>

          {/* Botón de comentar a la derecha - Solo si no hay comentarios */}
          {(allowInteractions && post.comments.length === 0) || canArchiveEvent ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                flexShrink: 0,
              }}
            >
              {allowInteractions && post.comments.length === 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="event-message-hover-action"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsCommenting(!isCommenting);
                  }}
                  sx={{
                    padding: "4px 8px",
                    gap: "4px",
                    color: theme.palette.text.secondary,
                    fontSize: "0.7rem",
                    opacity: 0.65,
                    transition: "opacity 0.2s ease, color 0.2s ease",
                    "&:hover": {
                      backgroundColor: "transparent",
                      color: theme.palette.primary.main,
                    },
                  }}
                >
                  <MessageCircle size={11} />
                  Comentar
                </Button>
              )}

              {canArchiveEvent && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="event-message-hover-action"
                  onClick={(e) => {
                    e.stopPropagation();
                    onArchiveEvent(post.id);
                  }}
                  sx={{
                    padding: "4px 8px",
                    gap: "4px",
                    color: theme.palette.text.secondary,
                    fontSize: "0.7rem",
                    opacity: 0.65,
                    transition: "opacity 0.2s ease, color 0.2s ease",
                    "&:hover": {
                      backgroundColor: "transparent",
                      color: theme.palette.primary.main,
                    },
                  }}
                >
                  <Archive size={11} />
                  Archivar
                </Button>
              )}
            </Box>
          ) : null}
        </Box>

        {/* Comentarios en eventos del sistema */}
        {(showComments && post.comments.length > 0) || (allowInteractions && isCommenting) ? (
          <Box
            sx={{ marginLeft: "12px", marginTop: "8px", position: "relative" }}
          >
            {/* Línea vertical conectando comentarios */}
            <Box
              sx={{
                position: "absolute",
                left: "-10px",
                top: "-8px",
                bottom: "6px",
                width: "1px",
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.1)"
                    : "rgba(0, 0, 0, 0.1)",
              }}
            />
            {/* Contenedor que envuelve todos los comentarios y el input */}
            <Box
              sx={{
                marginLeft: "10px",
                backgroundColor: theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.03)"
                  : "rgba(0, 0, 0, 0.02)",
                borderRadius: "8px",
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              {/* Comentarios existentes */}
              {showComments && post.comments.length > 0 && (
                <Box sx={{ padding: "12px" }}>
                  {post.comments.map((comment) => (
                    <ThreadComment
                      key={comment.id}
                      comment={comment}
                      getRoleDisplayName={getRoleDisplayName}
                      onDeleteAttachment={allowInteractions ? onDeleteAttachment : undefined}
                      currentUser={allowInteractions ? currentUser : null}
                      parentMessageId={post.id}
                      allowInteractions={allowInteractions}
                      onEditComment={allowInteractions ? onEditComment : undefined}
                      onDeleteComment={
                        allowInteractions ? onDeleteComment : undefined
                      }
                      onShowNotification={showNotification}
                      onLike={onLike}
                      conversationFontSize={conversationFontSize}
                    />
                  ))}
                </Box>
              )}

              {/* Input para nuevo comentario en eventos - Siempre visible si hay comentarios */}
              {allowInteractions && (post.comments.length > 0 || isCommenting) && (
                <Box
                  onClick={(e) => e.stopPropagation()}
                  sx={{
                    padding: "8px 12px",
                    paddingTop: showComments && post.comments.length > 0 ? "6px" : "8px",
                  }}
                >
                  {/* Mostrar archivos adjuntos si hay alguno */}
                  {commentAttachments.length > 0 && (
                    <Box sx={{ marginBottom: "4px" }}>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                        {commentAttachments.map((file, index) => (
                          <Box
                            key={`${file.name}-${index}`}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: "3px",
                              padding: "2px 5px",
                              backgroundColor: theme.palette.action.hover,
                              borderRadius: "4px",
                              border: `1px solid ${theme.palette.divider}`,
                              maxWidth: "120px",
                            }}
                          >
                            <Box
                              sx={{
                                color: theme.palette.primary.main,
                                flexShrink: 0,
                              }}
                            >
                              {file.type.startsWith("image/") ? (
                                <ImageIcon size={9} />
                              ) : file.type.startsWith("video/") ? (
                                <Video size={9} />
                              ) : (
                                <Paperclip size={9} />
                              )}
                            </Box>
                            <Typography
                              variant="caption"
                              title={file.name}
                              sx={{
                                fontSize: "0.6rem",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                flex: 1,
                              }}
                            >
                              {file.name}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() =>
                                setCommentAttachments(
                                  commentAttachments.filter((_, i) => i !== index)
                                )
                              }
                              sx={{
                                padding: "1px",
                                color: theme.palette.text.secondary,
                                "&:hover": {
                                  color: theme.palette.error.main,
                                },
                              }}
                            >
                              <X size={8} />
                            </IconButton>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}

                  {/* Input compacto con botones alineados a la misma altura */}
                  <Box
                    sx={{
                      display: "flex",
                      gap: "3px",
                      alignItems: "stretch",
                      width: "100%",
                      minWidth: 0,
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <CustomMentionsInput
                        placeholder="Escribe un comentario..."
                        value={newComment}
                        onChange={(event, newValue, newPlainTextValue, mentions) => {
                          setNewComment(newValue);
                        }}
                        onFocus={() => {
                          if (!composerFocused) {
                            setComposerFocused(true);
                          }
                        }}
                        users={mentionUsers}
                        suggestionsPortal={true}
                        suggestionsZIndex={1800}
                        sx={{
                          minHeight: "28px",
                          maxHeight: "100px",
                          fontSize: messageFontSize,
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && e.ctrlKey) {
                            e.preventDefault();
                            handleComment();
                          }
                        }}
                        onPaste={handlePasteImage}
                      />
                    </Box>

                    {/* Botones con la misma altura que el input */}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        gap: "2px",
                        flexShrink: 0,
                        alignItems: "center",
                        minHeight: "28px",
                      }}
                    >
                      {/* Botón adjuntar archivo */}
                      <AttachmentUploader
                        attachments={commentAttachments}
                        onAttachmentsChange={setCommentAttachments}
                        disabled={false}
                        compact={true}
                        iconOnly={true}
                      />

                      {/* Botón cancelar - solo si no hay comentarios previos */}
                      {post.comments.length === 0 && (
                        <IconButton
                          size="small"
                          onClick={() => {
                            setIsCommenting(false);
                            setNewComment("");
                            setCommentAttachments([]);
                          }}
                          title="Cancelar"
                          sx={{
                            padding: "5px",
                            color: theme.palette.text.secondary,
                            "&:hover": {
                              color: theme.palette.error.main,
                              backgroundColor: theme.palette.error.main + "10",
                            },
                          }}
                        >
                          <X size={12} />
                        </IconButton>
                      )}

                      {/* Botón enviar */}
                      <IconButton
                        size="small"
                        onClick={handleComment}
                        disabled={
                          !hasValidContent(newComment) &&
                          commentAttachments.length === 0
                        }
                        title="Enviar comentario"
                        sx={{
                          padding: "5px",
                          color: (hasValidContent(newComment) || commentAttachments.length > 0)
                            ? theme.palette.primary.main
                            : theme.palette.text.disabled,
                          backgroundColor: (hasValidContent(newComment) || commentAttachments.length > 0)
                            ? theme.palette.primary.main + "12"
                            : "transparent",
                          borderRadius: "6px",
                          "&:hover": {
                            backgroundColor: theme.palette.primary.main + "20",
                          },
                          "&:disabled": {
                            color: theme.palette.text.disabled,
                          },
                        }}
                      >
                        <Send size={12} />
                      </IconButton>
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        ) : null}
      </Box>
    );
  }

  return (
    <Box
      ref={(el) => setMessageRef && setMessageRef(post.id, el)}
      data-message-id={post.id}
      sx={{
        paddingTop: "16px",
        paddingBottom: "8px",
        borderBottom: `1px solid ${theme.palette.divider}`,
        "&:last-child": {
          borderBottom: "none",
        },
        ...highlightedStyles,
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        sx={{
          paddingX: "20px",
          width: "100%",
          alignItems: "stretch",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            position: "relative",
          }}
        >
          <Avatar
            src={author?.profileImage || undefined}
            sx={{ width: 32, height: 32 }}
          >
            <AvatarFallback
              sx={{
                backgroundColor: brand.avatar,
                color: "#fff",
                fontSize: "0.8rem",
                fontWeight: 600,
              }}
            >
              {author?.name
                ?.split(" ")
                .map((n) => n[0])
                .join("")
                .toUpperCase()
                .slice(0, 2) || "U"}
            </AvatarFallback>
          </Avatar>
          {/* Línea vertical que conecta con los comentarios */}
          {showComments && post.comments.length > 0 && (
            <Box
              sx={{
                width: "2px",
                backgroundColor: theme.palette.divider,
                marginTop: "8px",
                marginBottom: "4px",
                flexGrow: 1,
              }}
            />
          )}
        </Box>

        <Box sx={{ width: "100%", minWidth: 0, overflow: "hidden" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: "12px",
              marginBottom: "4px",
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  marginBottom: "4px",
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ fontWeight: 600, fontSize: authorFontSize }}
                >
                  {author?.name || "Usuario"}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontSize: timestampFontSize,
                  }}
                >
                  {getRelativeTime(post.timestamp)}
                </Typography>

                {/* Chip de tipo de mensaje */}
                {messageTypeInfo && (
                  <Chip
                    icon={messageTypeInfo.icon}
                    label={
                      post.messageType === "action"
                        ? `${messageTypeInfo.label} ${getAssignedConsultantName(
                          post.actionAssignedTo
                        )}`
                        : messageTypeInfo.label
                    }
                    size="small"
                    onClick={
                      post.messageType === "action"
                        ? (e) => {
                          e.stopPropagation();
                          setModalInitialType("action");
                          setShowEditMessageTypeModal(true);
                        }
                        : undefined
                    }
                    sx={{
                      height: "22px",
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      backgroundColor: messageTypeInfo.color.background,
                      color: messageTypeInfo.color.text,
                      border: `1px solid ${messageTypeInfo.color.border}`,
                      cursor:
                        post.messageType === "action" ? "pointer" : "default",
                      "&:hover":
                        post.messageType === "action"
                          ? {
                            backgroundColor: messageTypeInfo.color.border,
                          }
                          : {},
                      "& .MuiChip-label": {
                        padding: "0 6px",
                      },
                      "& .MuiChip-icon": {
                        marginLeft: "6px",
                        marginRight: "-2px",
                        color: messageTypeInfo.color.text,
                      },
                    }}
                  />
                )}

                {/* Indicador de resuelto - Solo para mensajes important o action */}
                {post.isResolved &&
                  (post.messageType === "important" ||
                    post.messageType === "action") && (
                    <Chip
                      icon={<Check size={14} />}
                      label="Resuelto"
                      size="small"
                      sx={{
                        height: "22px",
                        fontSize: "0.75rem",
                        fontWeight: 500,
                        backgroundColor: "#E3F6EA",
                        color: "#0F9D58",
                        border: "1px solid #C4E8D2",
                        "& .MuiChip-label": {
                          padding: "0 6px",
                        },
                        "& .MuiChip-icon": {
                          marginLeft: "6px",
                          marginRight: "-2px",
                          color: "#0F9D58",
                        },
                      }}
                    />
                  )}

                {post.tags.length > 0 && (
                  <Box sx={{ display: "flex", gap: "4px" }}>
                    {post.tags.map((tag, index) => {
                      const tagColor = getTagColor(tag);
                      return (
                        <Chip
                          key={index}
                          label={tag}
                          size="small"
                          sx={{
                            height: "20px",
                            fontSize: "0.75rem",
                            fontWeight: 500,
                            backgroundColor: tagColor.background,
                            color: tagColor.text,
                            border: `1px solid ${tagColor.border}`,
                            "& .MuiChip-label": {
                              padding: "0 6px",
                            },
                          }}
                        />
                      );
                    })}
                  </Box>
                )}
              </Box>

              <Box sx={{ position: "relative", marginBottom: "1px" }}>
                {isEditing ? (
                  <Box
                    data-prevent-expand="true"
                    onClick={(event) => event.stopPropagation()}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1,
                      width: "100%",
                      cursor: "default",
                      padding: 0,
                      margin: 0,
                    }}
                  >
                    <CustomTextArea
                      value={editedContent}
                      onChange={(e) => setEditedContent(e.target.value)}
                      placeholder="Escribe tu mensaje..."
                      autoFocus
                      rows={1}
                      maxRows={6}
                      autoExpand={true}
                      onKeyDown={(e) => {
                        if (e.key === "Escape") {
                          e.preventDefault();
                          handleCancelEdit();
                        }
                      }}
                      sx={{
                        flex: 1,
                        width: "100%",
                        minHeight: "32px",
                        lineHeight: 1.4,
                        fontSize: "0.9rem",
                      }}
                    />
                    <Box
                      data-prevent-expand="true"
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 0.5,
                        flexShrink: 0,
                        paddingTop: "8px",
                      }}
                    >
                      <IconButton
                        size="small"
                        data-prevent-expand="true"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleSaveEdit();
                        }}
                        title="Guardar"
                      >
                        <CheckCircle2 size={16} />
                      </IconButton>
                      <IconButton
                        size="small"
                        data-prevent-expand="true"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleCancelEdit();
                        }}
                        title="Cancelar"
                      >
                        <X size={16} />
                      </IconButton>
                    </Box>
                  </Box>
                ) : (
                  <Box
                    translate="no"
                    sx={{
                      marginBottom: "0px",
                      minWidth: 0,
                      maxWidth: "100%",
                    }}
                  >
                    <Typography
                      component="div"
                      variant="body2"
                      sx={{
                        ...messageContentTypographySx,
                        fontSize: messageFontSize,
                        display: "inline",
                      }}
                    >
                      {formatContent(displayPostContent)}
                    </Typography>
                    {shouldTruncatePost && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(event) => {
                          event.stopPropagation();
                          setShowFullPost((prev) => !prev);
                        }}
                        sx={{
                          padding: "2px 6px",
                          fontSize: "0.75rem",
                          color: theme.palette.primary.main,
                          marginLeft: "6px",
                          minWidth: "auto",
                          display: "inline-flex",
                          verticalAlign: "baseline",
                          "&:hover": {
                            backgroundColor: "transparent",
                            textDecoration: "underline",
                          },
                        }}
                      >
                        {showFullPost ? "Mostrar menos" : "Mostrar completo"}
                      </Button>
                    )}
                  </Box>
                )}
              </Box>

              {/* Archivos adjuntos */}
              {post.attachments && post.attachments.length > 0 && (
                <Box
                  sx={{
                    marginBottom: "8px",
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "4px",
                    width: "100%",
                    overflow: "hidden",
                  }}
                >
                  {post.attachments.map((attachment, index) => (
                    <AttachmentDisplay
                      key={attachment.id || index}
                      attachment={{
                        ...attachment,
                        messageId: post.id, // Pasar el ID del mensaje
                        message_id: post.id, // También en formato snake_case por compatibilidad
                      }}
                      canDelete={
                        allowInteractions &&
                        currentUser &&
                        String(currentUser.id) === String(post.authorId)
                      }
                      onDelete={(removedAttachmentId) => {
                        if (onDeleteAttachment) {
                          onDeleteAttachment({
                            messageId: post.id,
                            attachmentId: removedAttachmentId,
                            parentId: post.id,
                          });
                        }
                      }}
                    />
                  ))}
                </Box>
              )}

              {/* Acciones del post */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  marginTop: "0px",
                }}
              >
                {allowInteractions ? (
                  <>
                    <LikersTooltip likers={post.likedBy}>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          onLike && onLike(post.id);
                        }}
                        sx={{
                          padding: "2px 8px",
                          gap: "6px",
                          minWidth: "auto",
                          color: post.liked
                            ? theme.palette.primary.main
                            : theme.palette.text.secondary,
                          cursor: "pointer",
                          fontSize: "0.8rem",
                          fontWeight: 500,
                          "&:hover": {
                            backgroundColor: theme.palette.action.hover,
                            color: theme.palette.primary.main,
                          },
                        }}
                      >
                        <ThumbsUp
                          size={14}
                          fill={post.liked ? theme.palette.primary.main : "none"}
                        />
                        {likeCount > 0 && <span>{likeCount}</span>}
                      </Button>
                    </LikersTooltip>

                    {/* Solo mostrar botón Comentar si NO hay comentarios existentes */}
                    {post.comments.length === 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsCommenting(!isCommenting);
                        }}
                        sx={{
                          padding: "2px 8px",
                          gap: "6px",
                          minWidth: "auto",
                          color: theme.palette.text.secondary,
                          fontSize: "0.8rem",
                          fontWeight: 500,
                          "&:hover": {
                            backgroundColor: theme.palette.action.hover,
                            color: theme.palette.primary.main,
                          },
                        }}
                      >
                        <MessageCircle size={14} />
                        Comentar
                      </Button>
                    )}
                  </>
                ) : (
                  (likeCount > 0 || commentCount > 0) && (
                    <Stack direction="row" spacing={2} sx={{ color: theme.palette.text.secondary, fontSize: "0.8rem" }}>
                      {likeCount > 0 && (
                        <Box sx={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
                          <ThumbsUp size={14} />
                          <Typography variant="caption">{likeCount}</Typography>
                        </Box>
                      )}
                      {commentCount > 0 && (
                        <Box sx={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
                          <MessageCircle size={14} />
                          <Typography variant="caption">{commentCount}</Typography>
                        </Box>
                      )}
                    </Stack>
                  )
                )}
              </Box>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
              {/* Menú de tres puntos (solo si el usuario tiene permisos) */}
              {shouldShowMenu() && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <IconButton
                      size="small"
                      sx={{
                        width: "32px",
                        height: "32px",
                        padding: 0,
                        flexShrink: 0,
                        opacity: 0.6,
                        "&:hover": {
                          opacity: 1,
                          backgroundColor: theme.palette.action.hover,
                        },
                      }}
                    >
                      <MoreVertical size={16} />
                    </IconButton>
                  </DropdownMenuTrigger>

                  <DropdownMenuContent
                    align="end"
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                      minWidth: "180px",
                    }}
                  >
                    {allowMoveToNewThread && (
                      isAlreadySynchronizedThread ? (
                        <DropdownMenuItem disabled>
                          <LinkIcon size={16} />
                          Mensaje ya sincronizado
                        </DropdownMenuItem>
                      ) : (
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            onMoveToNewThread(post.id);
                          }}
                        >
                          <LinkIcon size={16} />
                          Mover/Sincronizar hilo
                        </DropdownMenuItem>
                      )
                    )}
                    {canToggleImportant() && (
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleImportant();
                        }}
                      >
                        <AlertTriangle size={16} />
                        {post.messageType === "important"
                          ? "Desmarcar como importante"
                          : "Marcar como importante"}
                      </DropdownMenuItem>
                    )}
                    {allowAssignTask && canAssignTask() && (
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAssignTask();
                        }}
                      >
                        <CheckSquare size={16} />
                        Asignar tarea
                      </DropdownMenuItem>
                    )}
                    {isOwnMessage && (
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit();
                        }}
                      >
                        <Edit size={16} />
                        Editar
                      </DropdownMenuItem>
                    )}
                    {isOwnMessage && (
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete();
                        }}
                        className="text-destructive"
                      >
                        <Trash2 size={16} />
                        Eliminar
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </Box>
          </Box>

          {/* Comentarios */}
          {(showComments && post.comments.length > 0) || (allowInteractions && isCommenting) ? (
            <Box
              sx={{
                marginTop: "0px",
                position: "relative",
                paddingBottom: "6px",
              }}
            >
              {/* Contenedor que envuelve todos los comentarios y el input */}
              <Box
                sx={{
                  padding: "12px",
                  backgroundColor: theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.03)"
                    : "rgba(0, 0, 0, 0.02)",
                  borderRadius: "8px",
                  border: `1px solid ${theme.palette.divider}`,
                }}
              >
                {/* Comentarios existentes */}
                {showComments && post.comments.length > 0 && (
                  <>
                    {post.comments.map((comment) => (
                      <ThreadComment
                        key={comment.id}
                        comment={comment}
                        getRoleDisplayName={getRoleDisplayName}
                        onDeleteAttachment={
                          allowInteractions ? onDeleteAttachment : undefined
                        }
                        currentUser={allowInteractions ? currentUser : null}
                        parentMessageId={post.id}
                        allowInteractions={allowInteractions}
                        onEditComment={allowInteractions ? onEditComment : undefined}
                        onDeleteComment={
                          allowInteractions ? onDeleteComment : undefined
                        }
                        onShowNotification={showNotification}
                        onLike={onLike}
                        conversationFontSize={conversationFontSize}
                      />
                    ))}
                  </>
                )}

                {/* Input para nuevo comentario - Siempre visible si hay comentarios, o si isCommenting */}
                {allowInteractions && (post.comments.length > 0 || isCommenting) && (
                  <Box
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                      marginTop: showComments && post.comments.length > 0 ? "6px" : "0",
                    }}
                  >
                    {/* Mostrar archivos adjuntos si hay alguno */}
                    {commentAttachments.length > 0 && (
                      <Box sx={{ marginBottom: "6px" }}>
                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: "4px" }}
                        >
                          {commentAttachments.map((file, index) => (
                            <Box
                              key={`${file.name}-${index}`}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                                padding: "3px 6px",
                                backgroundColor: theme.palette.action.hover,
                                borderRadius: "4px",
                                border: `1px solid ${theme.palette.divider}`,
                                maxWidth: "140px",
                              }}
                            >
                              <Box
                                sx={{
                                  color: theme.palette.primary.main,
                                  flexShrink: 0,
                                }}
                              >
                                {file.type.startsWith("image/") ? (
                                  <ImageIcon size={10} />
                                ) : file.type.startsWith("video/") ? (
                                  <Video size={10} />
                                ) : (
                                  <Paperclip size={10} />
                                )}
                              </Box>
                              <Typography
                                variant="caption"
                                title={file.name}
                                sx={{
                                  fontSize: "0.65rem",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  flex: 1,
                                }}
                              >
                                {file.name}
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={() =>
                                  setCommentAttachments(
                                    commentAttachments.filter(
                                      (_, i) => i !== index
                                    )
                                  )
                                }
                                sx={{
                                  padding: "1px",
                                  color: theme.palette.text.secondary,
                                  "&:hover": {
                                    color: theme.palette.error.main,
                                  },
                                }}
                              >
                                <X size={9} />
                              </IconButton>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}

                    {/* Input compacto con botones alineados a la misma altura */}
                    <Box
                      sx={{
                        display: "flex",
                        gap: "4px",
                        alignItems: "stretch",
                        width: "100%",
                        minWidth: 0,
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <CustomMentionsInput
                          placeholder="Escribe un comentario..."
                          value={newComment}
                          onChange={(
                            event,
                            newValue,
                            newPlainTextValue,
                            mentions
                          ) => {
                            setNewComment(newValue);
                          }}
                          onFocus={() => {
                            if (!composerFocused) {
                              setComposerFocused(true);
                            }
                          }}
                          users={mentionUsers}
                          suggestionsPortal={true}
                          suggestionsZIndex={1800}
                          sx={{
                            minHeight: "32px",
                            maxHeight: "120px",
                            fontSize: messageFontSize,
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && e.ctrlKey) {
                              e.preventDefault();
                              handleComment();
                            }
                          }}
                          onPaste={handlePasteImage}
                        />
                      </Box>

                      {/* Botones con la misma altura que el input */}
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          gap: "2px",
                          flexShrink: 0,
                          alignItems: "center",
                          minHeight: "32px",
                        }}
                      >
                        {/* Botón adjuntar archivo */}
                        <AttachmentUploader
                          attachments={commentAttachments}
                          onAttachmentsChange={setCommentAttachments}
                          disabled={false}
                          compact={true}
                          iconOnly={true}
                        />

                        {/* Botón cancelar - solo si no hay comentarios previos (primer comentario) */}
                        {post.comments.length === 0 && (
                          <IconButton
                            size="small"
                            onClick={() => {
                              setIsCommenting(false);
                              setNewComment("");
                              setCommentAttachments([]);
                            }}
                            title="Cancelar"
                            sx={{
                              padding: "6px",
                              color: theme.palette.text.secondary,
                              "&:hover": {
                                color: theme.palette.error.main,
                                backgroundColor: theme.palette.error.main + "10",
                              },
                            }}
                          >
                            <X size={14} />
                          </IconButton>
                        )}

                        {/* Botón enviar */}
                        <IconButton
                          size="small"
                          onClick={handleComment}
                          disabled={
                            !hasValidContent(newComment) &&
                            commentAttachments.length === 0
                          }
                          title="Enviar comentario"
                          sx={{
                            padding: "6px",
                            color: (hasValidContent(newComment) || commentAttachments.length > 0)
                              ? theme.palette.primary.main
                              : theme.palette.text.disabled,
                            backgroundColor: (hasValidContent(newComment) || commentAttachments.length > 0)
                              ? theme.palette.primary.main + "12"
                              : "transparent",
                            borderRadius: "6px",
                            "&:hover": {
                              backgroundColor: theme.palette.primary.main + "20",
                            },
                            "&:disabled": {
                              color: theme.palette.text.disabled,
                            },
                          }}
                        >
                          <Send size={14} />
                        </IconButton>
                      </Box>
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          ) : null}
        </Box>
      </Stack >
      {/* Header del post */}

      {/* Modal de confirmación para eliminar mensaje */}
      <ConfirmModal
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        title="Eliminar mensaje"
        question="¿Estás seguro de que quieres eliminar este mensaje?"
        description="Esta acción no se puede deshacer. Se eliminará permanentemente el mensaje y todos sus comentarios asociados."
        confirmLabel="Eliminar mensaje"
        loadingLabel="Eliminando..."
        loading={isDeleting}
        tone="danger"
      />

    </Box >
  );
}
